/**
 * Screen Share Live In Space Manager (factory function)
 *
 * Manages broadcasting screen share to space for Unreal Engine consumption.
 */

import {Socket} from "socket.io-client";
import {
  SpaceLiveBroadcastResult,
  SpaceLiveBroadcastStatus,
  StartSpaceLiveBroadcastRequest,
  StopSpaceLiveBroadcastRequest,
  BroadcastResponse,
  ParticipantInfo,
  RoomInfo,
} from "./types";
import {debugLog} from "../utils/debug";

// ─── Public handle interface ──────────────────────────────────────────────────

export interface ScreenShareLiveManagerHandle {
  readonly isActive: boolean;
  readonly broadcastProducerId: string | null;
  startBroadcast(
    room: RoomInfo,
    participant: ParticipantInfo,
    serverUrl: string
  ): Promise<SpaceLiveBroadcastResult>;
  stopBroadcast(room: RoomInfo | null, participantId?: string): Promise<void>;
  getStatus(
    roomId: string | null,
    callback: (status: SpaceLiveBroadcastStatus) => void
  ): void;
  onBroadcastAvailable(
    callback: (data: {
      roomId: string;
      producerId: string;
      producerUserId: string;
      userName: string;
    }) => void
  ): void;
  onBroadcastStopped(callback: (data: {roomId: string}) => void): void;
  removeListeners(): void;
  cleanup(): void;
}

// ─── Factory function (preferred) ────────────────────────────────────────────

export function createScreenShareLiveManager(
  socket: Socket
): ScreenShareLiveManagerHandle {
  let isBroadcasting = false;
  let currentProducerId: string | null = null;

  function findScreenshareProducerId(
    participant: ParticipantInfo
  ): string | null {
    for (const [producerId, producer] of participant.producers) {
      if (
        (producer as unknown as {appData?: {isScreenshare?: boolean}}).appData
          ?.isScreenshare
      ) {
        return producerId;
      }
    }
    return null;
  }

  async function startBroadcast(
    room: RoomInfo,
    participant: ParticipantInfo,
    serverUrl: string
  ): Promise<SpaceLiveBroadcastResult> {
    if (!room || !participant) {
      throw new Error("Must be in a room to start space live broadcast");
    }

    const screenshareProducerId = findScreenshareProducerId(participant);
    if (!screenshareProducerId) {
      throw new Error("No active screen share to broadcast to space");
    }

    return new Promise((resolve, reject) => {
      const request: StartSpaceLiveBroadcastRequest = {
        roomId: room.id,
        producerId: screenshareProducerId,
        participantId: participant.participantId,
        userName: participant.userName || "Unknown",
      };

      const timestamp = Date.now();

      socket.emit(
        "start-space-live-broadcast",
        request,
        (response: BroadcastResponse) => {
          if (response.error) {
            reject(new Error(response.error));
          } else {
            isBroadcasting = true;
            currentProducerId = screenshareProducerId;
            debugLog("SPACE-LIVE", "Started space live broadcast", {
              producerId: screenshareProducerId,
            });
            resolve({
              producerId: screenshareProducerId,
              roomId: room.id,
              serverUrl,
              timestamp,
            });
          }
        }
      );
    });
  }

  async function stopBroadcast(
    room: RoomInfo | null,
    participantId?: string
  ): Promise<void> {
    if (!room) {
      isBroadcasting = false;
      currentProducerId = null;
      return;
    }

    return new Promise((resolve) => {
      const request: StopSpaceLiveBroadcastRequest = {
        roomId: room.id,
        participantId,
      };

      socket.emit("stop-space-live-broadcast", request, () => {
        isBroadcasting = false;
        currentProducerId = null;
        debugLog("SPACE-LIVE", "Stopped space live broadcast");
        resolve();
      });
    });
  }

  function getStatus(
    roomId: string | null,
    callback: (status: SpaceLiveBroadcastStatus) => void
  ): void {
    if (!roomId) {
      callback({isActive: false});
      return;
    }
    socket.emit("get-space-live-broadcast-status", {roomId}, callback);
  }

  function onBroadcastAvailable(
    callback: (data: {
      roomId: string;
      producerId: string;
      producerUserId: string;
      userName: string;
    }) => void
  ): void {
    socket.on("space-live-broadcast-available", callback);
  }

  function onBroadcastStopped(
    callback: (data: {roomId: string}) => void
  ): void {
    socket.on("space-live-broadcast-stopped", callback);
  }

  function removeListeners(): void {
    socket.off("space-live-broadcast-available");
    socket.off("space-live-broadcast-stopped");
  }

  function cleanup(): void {
    isBroadcasting = false;
    currentProducerId = null;
    removeListeners();
  }

  return {
    get isActive() {
      return isBroadcasting;
    },
    get broadcastProducerId() {
      return currentProducerId;
    },
    startBroadcast,
    stopBroadcast,
    getStatus,
    onBroadcastAvailable,
    onBroadcastStopped,
    removeListeners,
    cleanup,
  };
}
